import { useCallback, useMemo, useState } from "react";

import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import { MappableField } from "lib/generated/integration-api";
import {
  useParagonCrmMapping,
  useParagonCrmRecordForm,
} from "lib/hooks/api/integrations/useParagonCrmRecordForm";
import { buildRecordForm, CrmRecordForm } from "lib/integrations/paragonCrm/buildRecordForm";
import {
  CrmPerson,
  CrmRecordKind,
  CrmRecordSeed,
  PERSON_FIELD_KEYS,
  personSeed,
} from "lib/integrations/paragonCrm/recordFields";
import { CrmPushRequest } from "lib/integrations/paragonCrm/types";

type Args = {
  provider: ParagonCrmProvider;
  recordKind: CrmRecordKind;
  /** The record's own details, which the person's are seeded on top of. */
  recordSeed: CrmRecordSeed;
  /** The contacts saved to the record, which is what there is to choose from. */
  people: CrmPerson[];
  /** CRM fields a push has already been refused for want of, collected onto the form. */
  demandedCrmFields: string[];
};

export type CrmSendPerson = {
  form?: CrmRecordForm;
  /** The object's own fields, for naming one the form has no control for. */
  crmFields: MappableField[];
  isLoading: boolean;
  isConfigured: boolean;
  error?: Error;
  /** Who the record is being sent as, once that is settled. */
  person?: CrmPerson;
  choose: (id: string | undefined) => void;
  /** The chosen contact as a mapped push of its own, for an object that holds no person. */
  personRequest?: CrmPushRequest;
  /** Whether anyone has to be chosen, which decides if the chooser is shown at all. */
  needsAPerson: boolean;
  /** The object is refused without a contact, rather than merely having fields describing one. */
  objectRequiresAPerson: boolean;
  /** There is no contact mapping to build the person from, which is a dead end. */
  needsAContactMapping: boolean;
  /** Send must stay disabled: nobody is chosen yet, or what they would be sent as is not known. */
  isBlocked: boolean;
  /** Forgets the choice, for a form that stays mounted while closed. */
  forget: () => void;
};

/**
 * The form a record is sent on, and who it is being sent as.
 *
 * One hook because the two decide each other: the person's details seed the form, and the object
 * the form was built from says whether a person is needed at all. Gathered out of the modal so
 * that reading them in the wrong order — offering Send before the last of them lands — is not
 * something a caller can do.
 */
export function useCrmSendPerson({
  provider,
  recordKind,
  recordSeed,
  people,
  demandedCrmFields,
}: Args): CrmSendPerson {
  /**
   * Left unset while there is a choice to make: picking the first of several would decide which
   * person a salesperson then works, which is not a decision a default should make quietly.
   */
  const [personId, setPersonId] = useState<string>();

  // Derived rather than stored, so a contact leaving the record cannot leave a chosen id pointing
  // at nobody — which disabled Send while the card below named whoever was left.
  const person =
    people.length === 1 ? people[0] : people.find((candidate) => candidate.id === personId);

  const seed = useMemo(() => ({ ...recordSeed, ...personSeed(person) }), [recordSeed, person]);

  const { form, crmFields, isLoading, isConfigured, error } = useParagonCrmRecordForm(
    recordKind,
    seed,
    true,
    demandedCrmFields,
  );

  // A HubSpot lead holds none of a person's fields but is refused without one, so the mapping
  // cannot signal it and the provider has to say so instead.
  const objectRequiresAPerson =
    !!form && !!provider.objectsRequiringAPerson?.includes(form.object.logicalName.toLowerCase());

  // Read from the mapping rather than the object: a company free to map a surname and choosing not
  // to should not be asked for one. Never for a contact, which is the person already.
  const needsAPerson =
    recordKind !== "contact" &&
    !!form &&
    (objectRequiresAPerson || PERSON_FIELD_KEYS.some((key) => key in form.crmFieldByKey));

  // The person is pushed as a contact of their own, through the same mapping and the same builder
  // the contact push uses, so nothing here decides which of the CRM's fields their details go to.
  const { mapping: savedContactMapping, isLoading: isLoadingContactMapping } = useParagonCrmMapping(
    "contact",
    objectRequiresAPerson,
  );

  // A mapping saved with nothing in it is the same dead end as having none: the contact built from
  // it would carry none of the person's details, and the lead cannot be created without a contact.
  const contactMapping =
    savedContactMapping && Object.keys(savedContactMapping.fields).length > 0
      ? savedContactMapping
      : undefined;

  // Stable, because a caller clears the choice from an effect and a new identity each render would
  // run it again on every one.
  const forget = useCallback(() => setPersonId(undefined), []);

  const personRequest = useMemo(() => {
    if (!objectRequiresAPerson || !person || !contactMapping) return undefined;
    const contactForm = buildRecordForm(
      "contact",
      contactMapping,
      personSeed(person),
      provider.lookupTargets,
    );
    return contactForm.toPushRequest(contactForm.initialData);
  }, [objectRequiresAPerson, person, contactMapping, provider.lookupTargets]);

  // A choice that exists has to be made; one that does not cannot be. With nobody on the record the
  // person's fields go empty, which is for the CRM to refuse if it insists on them — except for an
  // object refused without a contact, where Send stays blocked with nobody to choose.
  const awaitingAChoice = needsAPerson && !person && (objectRequiresAPerson || people.length > 0);

  return {
    form,
    crmFields,
    isLoading,
    isConfigured,
    error,
    person,
    choose: setPersonId,
    personRequest,
    needsAPerson,
    objectRequiresAPerson,
    needsAContactMapping: objectRequiresAPerson && !isLoadingContactMapping && !contactMapping,
    isBlocked: awaitingAChoice,
    forget,
  };
}
